/**
 * Labstep
 *
 * @module grid/BatchCreateResourceItems
 */

import { useGridBatchCreateResourceItemsServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { GridBatchCreateResourceItemsAddRow } from 'labstep-web/grid/BatchCreateResourceItems/components/AddRow';
import { useBatchCreateItemContext } from 'labstep-web/grid/BatchCreateResourceItems/context';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { GridBatchCreateResourceItemsProps } from 'labstep-web/grid/BatchCreateResourceItems/types';
import type React from 'react';
import { useMemo } from 'react';

export const GridBatchCreateResourceItems: React.FC<
  GridBatchCreateResourceItemsProps
> = ({ resource }) => {
  const context = useBatchCreateItemContext();
  const gridBatchCreateResourceItemsService =
    useGridBatchCreateResourceItemsServiceFromContext();

  if (!context) {
    return <div></div>;
  }

  const { data, setData, dateFormats } = context;

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: () => (
            <GridBatchCreateResourceItemsAddRow resource={resource} />
          ),
          align: 'left',
        },
      ],
    }),
    [],
  );
  const handleAddExtraRowIfLast = (params: any) => {
    const currentRowIndex = params.node?.rowIndex;
    const isLastRow = currentRowIndex >= data.length - 2;

    if (isLastRow) {
      const currentRow = { ...data[currentRowIndex] };

      // Generate name for current row if it's empty
      if (!currentRow.name || currentRow.name.trim() === '') {
        const name = resource.custom_identifier
          ? `${resource.custom_identifier} ${resource.name || ''}`
          : resource.name || '';
        const displayName = resource.displayName || name;
        const existingItemsWithNames = data.filter(
          (row, idx) => idx < currentRowIndex && row.name,
        ).length;
        const itemNumber =
          resource.resource_item_count + existingItemsWithNames + 1;
        currentRow.name = `${displayName} #${itemNumber}`;

        // Initialize errors for required fields since the row now has a name
        currentRow.errors =
          GridBatchCreateResourceItemsService.initializeRequiredFieldErrors(
            currentRow,
            currentRowIndex,
          );

        // Update the current row with the generated name
        const updatedData = [
          ...data.slice(0, currentRowIndex),
          currentRow,
          ...data.slice(currentRowIndex + 1),
        ];
        setData(updatedData);

        // If this is one of the last two rows, add a new blank row using the service
        if (isLastRow && gridBatchCreateResourceItemsService) {
          setTimeout(() => {
            gridBatchCreateResourceItemsService.addRows(
              updatedData,
              setData,
              resource,
              dateFormats,
              1,
            );
          }, 0);
        }
        const event = params.event;
        const key = event?.key;
        const colId = params.column.getColId();

        // After updating, restart editing with charPress to continue typing
        setTimeout(() => {
          const isPrintableChar =
            key &&
            key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey;
          params.api.startEditingCell({
            rowIndex: currentRowIndex,
            colKey: colId,
            charPress: isPrintableChar ? key : null,
          });
        }, 50);
      }
    }
  };

  const processDataFromClipboard = (
    params: any,
  ): string[][] | null => {
    if (!params.api) {
      return params.data;
    }

    const focusedCell = params.api.getFocusedCell();
    if (!focusedCell) {
      return params.data;
    }

    const focusedRowIndex = focusedCell.rowIndex;
    const pastedRowCount = params.data.length;
    const currentRowCount = data.length;

    // Calculate how many rows we need to add
    const rowsNeeded =
      focusedRowIndex + pastedRowCount - currentRowCount;

    if (rowsNeeded > 0) {
      // Add the required number of rows using AG Grid's transaction API
      const newRows =
        GridBatchCreateResourceItemsService.getInitialData(
          resource,
          rowsNeeded,
          undefined,
        );

      params.api.applyTransaction({
        add: newRows,
      });
    }

    return params.data;
  };

  return (
    <DataGrid
      height={'100%'}
      width={'100%'}
      defaultColDef={
        GridBatchCreateResourceItemsService.defaultColDef
      }
      columnDefs={GridBatchCreateResourceItemsService.getDefaultColDefs(
        resource,
        data,
        setData,
        dateFormats,
      )}
      rowData={data}
      suppressRowClickSelection
      suppressLastEmptyLineOnPaste={false}
      processDataFromClipboard={processDataFromClipboard}
      onPasteEnd={(params) => {
        // Sync the grid data back to state after paste
        const allRowData: any[] = [];
        params.api.forEachNode((node) => {
          allRowData.push(node.data);
        });
        setData(allRowData);
      }}
      onCellEditingStarted={handleAddExtraRowIfLast}
      onCellValueChanged={(params) => {
        const newValue = params.newValue || '';
        const rowData = params.node?.data;

        // Skip validation for empty rows (rows without names)
        if (!rowData?.name || rowData.name.trim() === '') {
          return;
        }

        if (
          newValue === '' &&
          GridBatchCreateResourceItemsService.dataIsRequired(
            params,
            data,
          )
        ) {
          GridBatchCreateResourceItemsService.insertErrorInData(
            data,
            setData,
            params,
            `This field is required.`,
          );
        }
      }}
    />
  );
};
